import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
} from "@mui/material";

import { ShipperUpgradeNowButton } from "components/Shipper/components/ShipperUpgradeNowButton";
import { useSubscriptionStatus } from "hooks/useSubscriptionStatus";
import { shipperSubscriptionTierWithoutPrivateShipments } from "utils";

export type ShipmentVisibility = "PUBLIC" | "PRIVATE" | "DIRECT_BOOKING";

const DIRECT_BOOKING_TOOLTIP =
  "Assign a shipment to a carrier or record an existing booking.";

interface VisibilityRadioGroupProps {
  visibility: ShipmentVisibility;
  onVisibilityChange: (visibility: ShipmentVisibility) => void;
  // Disables "Direct Booking" with this reason as its tooltip.
  directBookingDisabledReason: string | null;
}

export function VisibilityRadioGroup(props: VisibilityRadioGroupProps) {
  const { visibility, onVisibilityChange, directBookingDisabledReason } = props;

  const { subscription } = useSubscriptionStatus();

  if (!subscription) {
    return null;
  }

  const hasLimitedSubscription =
    shipperSubscriptionTierWithoutPrivateShipments(subscription);

  const onRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onVisibilityChange(event.target.value as ShipmentVisibility);
  };

  return (
    <FormControl>
      <RadioGroup value={visibility} onChange={onRadioChange}>
        <FormControlLabel value="PUBLIC" control={<Radio />} label="Public" />
        <Stack direction="row" alignItems="center">
          <FormControlLabel
            value="PRIVATE"
            control={<Radio disabled={hasLimitedSubscription} />}
            label="Private"
          />
          {hasLimitedSubscription && (
            <ShipperUpgradeNowButton
              buttonSize="small"
              trackingSource="create_shipment_form:private_shipment_paywall"
            />
          )}
        </Stack>
        {/* No subscription gate: every tier can post a shipment already
        booked. */}
        <Tooltip
          title={directBookingDisabledReason ?? DIRECT_BOOKING_TOOLTIP}
          placement="right"
        >
          <FormControlLabel
            value="DIRECT_BOOKING"
            control={<Radio disabled={directBookingDisabledReason !== null} />}
            label="Direct Booking"
          />
        </Tooltip>
      </RadioGroup>
    </FormControl>
  );
}
